import CallEndIcon from "@mui/icons-material/CallEnd"
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Divider from "@mui/material/Divider"
import MenuItem from "@mui/material/MenuItem"
import Paper from "@mui/material/Paper"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { Device } from "@twilio/voice-sdk"
import { useCallback, useEffect, useRef, useState } from "react"

import { useAuthorizedAxiosClient } from "../../common/axios"
import Column from "../../common/components/Column"
import Row from "../../common/components/Row"
import { environmentIsDevelop } from "../../common/utils"
import {
  POD_SESSION_LABELS,
  POD_STATE_LABELS,
  apiErrorMessage,
  formatNextBlock,
  formatSeconds,
  secondsSince,
} from "./podDialer"

const POLL_MS = 1000

function LeadCard({ dial, state, dispositions, onSaved, setError }) {
  const axios = useAuthorizedAxiosClient()
  const [disposition, setDisposition] = useState(dial.disposition || "")
  const [notes, setNotes] = useState(dial.notes || "")
  const [isSaving, setIsSaving] = useState(false)
  const [channel, setChannel] = useState("")
  const [templates, setTemplates] = useState([])
  const [templateId, setTemplateId] = useState("")
  const [message, setMessage] = useState("")
  const [emailTo, setEmailTo] = useState(dial.lead.email || "")
  const [subject, setSubject] = useState("")
  const [isSending, setIsSending] = useState(false)
  const [sent, setSent] = useState("")

  useEffect(() => {
    if (!channel) return
    axios
      .get("/pods/api/agent/templates/", {
        params: { dial: dial.dial_id, channel },
      })
      .then(({ data }) => setTemplates(data || []))
      .catch(() => setTemplates([]))
    setTemplateId("")
    setMessage("")
    setSubject("")
  }, [axios, channel, dial.dial_id])

  function pickTemplate(id) {
    setTemplateId(id)
    const template = templates.find((t) => t.id === id)
    if (!template) return
    if (channel === "email") {
      setSubject(template.subject || "")
      setMessage(template.html_body || "")
    } else {
      setMessage(template.text || "")
    }
  }

  function saveDisposition() {
    setIsSaving(true)
    axios
      .post(`/pods/api/agent/dials/${dial.dial_id}/disposition/`, {
        disposition,
        notes,
      })
      .then(() => onSaved())
      .catch((err) => setError(apiErrorMessage(err, "Could not save.")))
      .finally(() => setIsSaving(false))
  }

  function sendFollowUp() {
    setIsSending(true)
    const request =
      channel === "email"
        ? axios.post(`/pods/api/agent/dials/${dial.dial_id}/email/`, {
            to: emailTo,
            subject,
            html: message,
          })
        : axios.post(`/pods/api/agent/dials/${dial.dial_id}/text/`, {
            message,
            template_id: templateId || null,
          })
    request
      .then(() => {
        setSent(channel === "email" ? "Email sent." : "Text sent.")
        setChannel("")
      })
      .catch((err) => setError(apiErrorMessage(err, "Could not send.")))
      .finally(() => setIsSending(false))
  }

  function hangUp() {
    axios
      .post(`/pods/api/agent/dials/${dial.dial_id}/hang-up/`)
      .catch(() => setError("Could not hang up."))
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Column gap={2}>
        <Row
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Column gap={0.5}>
            <Typography variant="overline" color="text.secondary">
              Calling for{" "}
              {dial.client.multiple_campaigns
                ? `${dial.client.name} — ${dial.client.campaign}`
                : dial.client.name}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {dial.lead.name || dial.lead.phone}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[dial.lead.office_name, dial.lead.phone]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Column>
          {state === "on_call" ? (
            <Button
              color="error"
              variant="contained"
              startIcon={<CallEndIcon />}
              onClick={hangUp}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Hang up
            </Button>
          ) : null}
        </Row>
        {dial.history.length ? (
          <Column gap={0.5}>
            <Typography variant="subtitle2">Earlier calls</Typography>
            {dial.history.map((past) => (
              <Typography key={past.at} variant="body2" color="text.secondary">
                {new Date(past.at).toLocaleDateString()} ·{" "}
                {past.disposition || past.status}
                {past.notes ? ` — ${past.notes}` : ""}
              </Typography>
            ))}
          </Column>
        ) : null}
        <Divider />
        <Row sx={{ gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
          <TextField
            select
            size="small"
            label="Result"
            value={disposition}
            onChange={(e) => setDisposition(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            {dispositions.map((d) => (
              <MenuItem key={d.value} value={d.value}>
                {d.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            sx={{ flex: 1, minWidth: 240 }}
          />
          <LoadingButton
            variant="contained"
            loading={isSaving}
            disabled={!disposition}
            onClick={saveDisposition}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Save
          </LoadingButton>
        </Row>
        <Row sx={{ gap: 1 }}>
          <Button
            size="small"
            variant={channel === "sms" ? "contained" : "outlined"}
            onClick={() => setChannel(channel === "sms" ? "" : "sms")}
            sx={{ textTransform: "none" }}
          >
            Follow up by text
          </Button>
          <Button
            size="small"
            variant={channel === "email" ? "contained" : "outlined"}
            disabled={!dial.client.has_mailbox}
            title={
              dial.client.has_mailbox
                ? undefined
                : "This client has no connected mailbox."
            }
            onClick={() => setChannel(channel === "email" ? "" : "email")}
            sx={{ textTransform: "none" }}
          >
            Follow up by email
          </Button>
        </Row>
        {sent ? <Alert severity="success">{sent}</Alert> : null}
        {channel ? (
          <Column gap={1.5}>
            <TextField
              select
              size="small"
              label="Template"
              value={templateId}
              onChange={(e) => pickTemplate(e.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
            {channel === "email" ? (
              <>
                <TextField
                  size="small"
                  label="To"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                />
                <TextField
                  size="small"
                  label="Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </>
            ) : null}
            <TextField
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              minRows={4}
            />
            <LoadingButton
              variant="contained"
              loading={isSending}
              disabled={!message.trim()}
              onClick={sendFollowUp}
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              {channel === "email" ? "Send email" : "Send text"}
            </LoadingButton>
          </Column>
        ) : null}
      </Column>
    </Paper>
  )
}

/**
 * Pod agent dialer: connect once, then Ready/Pause. The schedule and pacer
 * decide who is called; a connected lead pops onto the card.
 */
export default function PodDialerPanel() {
  const axios = useAuthorizedAxiosClient()
  const [me, setMe] = useState(null)
  const [error, setError] = useState("")
  const [isConnecting, setIsConnecting] = useState(false)
  const [now, setNow] = useState(Date.now())
  const deviceRef = useRef(null)
  const callRef = useRef(null)

  const load = useCallback(
    () =>
      axios
        .get("/pods/api/agent/me/")
        .then(({ data }) => setMe(data))
        .catch(() => setError("Could not load the dialer.")),
    [axios]
  )

  useEffect(() => {
    load()
    const id = window.setInterval(() => {
      load()
      setNow(Date.now())
    }, POLL_MS)
    return () => window.clearInterval(id)
  }, [load])

  useEffect(
    () => () => {
      callRef.current?.disconnect()
      deviceRef.current?.destroy()
    },
    []
  )

  async function connect() {
    setIsConnecting(true)
    setError("")
    try {
      const { data } = await axios.get("/pods/api/agent/voice-token/")
      if (!data.session_id) {
        setError("No dialing session is running right now.")
        return
      }
      deviceRef.current?.destroy()
      const device = new Device(data.token, {
        logLevel: environmentIsDevelop() ? 1 : 5,
        codecPreferences: ["opus", "pcmu"],
      })
      deviceRef.current = device
      const call = await device.connect({
        params: { PodSessionId: String(data.session_id), To: "pod" },
      })
      callRef.current = call
      call.on("disconnect", () => {
        callRef.current = null
        load()
      })
    } catch (err) {
      setError(apiErrorMessage(err, "Could not connect your headset."))
    } finally {
      setIsConnecting(false)
    }
  }

  function disconnect() {
    callRef.current?.disconnect()
    callRef.current = null
  }

  function setState(state) {
    axios
      .post("/pods/api/agent/state/", { state })
      .then(load)
      .catch((err) => setError(apiErrorMessage(err, "Could not change state.")))
  }

  if (!me) return null
  if (!me.pod) {
    return <Alert severity="info">You are not in a pod yet.</Alert>
  }

  const session = me.session
  const state = me.me?.state || "offline"
  const connected = state !== "offline"
  const inState = secondsSince(me.me?.since, now)

  return (
    <Column gap={2} sx={{ pt: 2 }}>
      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Row
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Column gap={0.5}>
            <Typography variant="h6" fontWeight={700}>
              {me.pod.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {session
                ? POD_SESSION_LABELS[session.status] || session.status
                : formatNextBlock(me.next_block_at)}
            </Typography>
          </Column>
          <Row sx={{ alignItems: "center", gap: 1.5 }}>
            <Chip
              label={`${POD_STATE_LABELS[state] || state}${
                connected ? ` · ${formatSeconds(inState)}` : ""
              }`}
              color={
                state === "ready"
                  ? "success"
                  : state === "on_call"
                  ? "primary"
                  : "default"
              }
            />
            {!connected ? (
              <LoadingButton
                variant="contained"
                startIcon={<HeadsetMicOutlinedIcon />}
                loading={isConnecting}
                disabled={!session}
                onClick={connect}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Connect and go ready
              </LoadingButton>
            ) : null}
            {state === "ready" ? (
              <Button variant="outlined" onClick={() => setState("paused")}>
                Take a break
              </Button>
            ) : null}
            {state === "paused" || state === "wrap_up" ? (
              <Button variant="contained" onClick={() => setState("ready")}>
                {state === "wrap_up" ? "Ready for next call" : "Ready"}
              </Button>
            ) : null}
            {connected && state !== "on_call" ? (
              <Button color="inherit" onClick={disconnect}>
                Disconnect
              </Button>
            ) : null}
          </Row>
        </Row>
        {state === "wrap_up" ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Back to ready automatically in{" "}
            {formatSeconds((me.me.wrap_up_seconds || 0) - inState)}.
          </Typography>
        ) : null}
      </Paper>
      {me.dial ? (
        <LeadCard
          key={me.dial.dial_id}
          dial={me.dial}
          state={state}
          dispositions={me.dispositions || []}
          onSaved={load}
          setError={setError}
        />
      ) : connected ? (
        <Typography color="text.secondary">
          Waiting for the next live answer…
        </Typography>
      ) : null}
    </Column>
  )
}
